<script setup lang="ts">
import { inject, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery } from '@tanstack/vue-query';
import { type SerializedUser } from '@/api/users';
import { officesApi } from '@/api/offices';
import { serviceCompaniesApi } from '@/api/service-companies';
import InfoTooltip from '@/components/info-tooltip.vue';
import { useTableFilter } from '@/composables/useTableFilter';

const { t } = useI18n();

const displayNameFilter = useTableFilter(['displayName']);

const currentUser = inject<Ref<SerializedUser>>('currentUser');

interface Props {
  employmentContractType?: string;
  workdayType?: string;
  agreedOvertimeCompensation?: boolean;
  compensationType?: string;
  compensationTypeErrors?: string;
  flexibleWorkingHours?: boolean;
  exceptionalWorkdayResolution?: string;
  exceptionalWorkdayDate?: string;
  exceptionalWorkdayText?: string;
  officeId?: number;
  serviceCompanyId?: number;
}

interface Emits {
  (e: 'update:employmentContractType', value: string | null | undefined): void;
  (e: 'update:workdayType', value: string | null | undefined): void;
  (e: 'update:agreedOvertimeCompensation', value: boolean | null | undefined): void;
  (e: 'update:compensationType', value: string | null | undefined): void;
  (e: 'update:flexibleWorkingHours', value: boolean | null | undefined): void;
  (e: 'update:exceptionalWorkdayResolution', value: string | null | undefined): void;
  (e: 'update:exceptionalWorkdayDate', value: string | null | undefined): void;
  (e: 'update:exceptionalWorkdayText', value: string | null | undefined): void;
  (e: 'update:officeId', value: number | null | undefined): void;
  (e: 'update:serviceCompanyId', value: number | null | undefined): void;
}

defineProps<Props>();
const emit = defineEmits<Emits>();

const workdayTypes = [
  { title: t('caregiverForm.workdayTypes.fixed'), value: 'fixed' },
  { title: t('caregiverForm.workdayTypes.shifts'), value: 'shifts' },
  { title: t('caregiverForm.workdayTypes.cyclic'), value: 'cyclic' },
  { title: t('caregiverForm.workdayTypes.biweekly'), value: 'biweekly' },
  { title: t('caregiverForm.workdayTypes.exceptional'), value: 'exceptional' },
  { title: t('caregiverForm.workdayTypes.partial'), value: 'partial' },
];

const compensationTypes = [
  { title: t('caregiverForm.compensationTypes.financial'), value: 'financial' },
  { title: t('caregiverForm.compensationTypes.daysOff'), value: 'days_off' },
];

const { data: offices } = useQuery({
  queryKey: ['offices'],
  queryFn: () => officesApi.index(),
  enabled: !!currentUser?.value?.companyAuditable,
});

const { data: serviceCompanies } = useQuery({
  queryKey: ['serviceCompanies'],
  queryFn: () => serviceCompaniesApi.index(),
  enabled: !!currentUser?.value?.companyAuditable,
});

</script>

<template>
  <v-card class="mb-6">
    <v-card-item>
      <v-card-title class="text-subtitle-1 font-weight-bold">
        {{ t('caregiver.auditableFields') }}
      </v-card-title>
    </v-card-item>
    <v-card-text class="pt-4">
      <p class="mb-4">
        {{ t('caregiver.auditableFieldsDescription') }}
      </p>
      <v-radio-group
        :model-value="employmentContractType"
        :label="t('caregiver.employmentContractType')"
        item-title="title"
        item-value="value"
        inline
        @update:model-value="emit('update:employmentContractType', $event)"
      >
        <v-radio
          :label="t('caregiverForm.employmentContractTypes.contract')"
          value="contract"
        />
        <v-radio
          :label="t('caregiverForm.employmentContractTypes.taxBill')"
          value="tax_bill"
        />
        <v-radio
          :label="t('caregiverForm.employmentContractTypes.withoutContract')"
          value="without_contract"
        />
      </v-radio-group>
      <div v-if="employmentContractType === 'contract'">
        <v-select
          :model-value="workdayType === 'not_defined' ? null : workdayType"
          :label="t('caregiver.workdayType')"
          variant="outlined"
          :items="workdayTypes"
          @update:model-value="emit('update:workdayType', $event)"
        >
          <template #append>
            <info-tooltip :message="t('caregiverForm.AuditableDescription.workdayType')" />
          </template>
        </v-select>
        <v-checkbox
          :model-value="agreedOvertimeCompensation"
          color="primary"
          :label="t('caregiver.agreedOvertimeCompensation')"
          @update:model-value="emit('update:agreedOvertimeCompensation', $event)"
        >
          <template #append>
            <info-tooltip :message="t('caregiverForm.AuditableDescription.agreedOvertimeCompensation')" />
          </template>
        </v-checkbox>

        <v-select
          v-if="agreedOvertimeCompensation"
          :model-value="compensationType"
          :label="t('caregiver.compensationType')"
          variant="outlined"
          :items="compensationTypes"
          :error-messages="compensationTypeErrors"
          @update:model-value="emit('update:compensationType', $event)"
        >
          <template #append>
            <info-tooltip :message="t('caregiverForm.AuditableDescription.compensationType')" />
          </template>
        </v-select>

        <v-checkbox
          :model-value="flexibleWorkingHours"
          color="primary"
          :label="t('caregiver.flexibleWorkingHours')"
          @update:model-value="emit('update:flexibleWorkingHours', $event)"
        >
          <template #append>
            <info-tooltip :message="t('caregiverForm.AuditableDescription.flexibleWorkingHours')" />
          </template>
        </v-checkbox>

        <v-text-field
          :model-value="exceptionalWorkdayResolution"
          variant="outlined"
          :label="t('caregiver.exceptionalWorkdayResolution')"
          @update:model-value="emit('update:exceptionalWorkdayResolution', $event)"
        >
          <template #append>
            <info-tooltip :message="t('caregiverForm.AuditableDescription.exceptionalWorkdayResolution')" />
          </template>
        </v-text-field>

        <v-text-field
          :model-value="exceptionalWorkdayDate"
          type="date"
          variant="outlined"
          :label="t('caregiver.exceptionalWorkdayDate')"
          @update:model-value="emit('update:exceptionalWorkdayDate', $event)"
        >
          <template #append>
            <info-tooltip :message="t('caregiverForm.AuditableDescription.exceptionalWorkdayDate')" />
          </template>
        </v-text-field>

        <v-textarea
          :model-value="exceptionalWorkdayText"
          variant="outlined"
          :label="t('caregiver.exceptionalWorkdayText')"
          @update:model-value="emit('update:exceptionalWorkdayText', $event)"
        >
          <template #append>
            <info-tooltip :message="t('caregiverForm.AuditableDescription.exceptionalWorkdayText')" />
          </template>
        </v-textarea>

        <v-autocomplete
          :model-value="officeId"
          variant="outlined"
          item-title="displayName"
          item-value="id"
          :custom-filter="displayNameFilter"
          :label="t('caregiver.officeId')"
          :items="offices"
          @update:model-value="emit('update:officeId', $event)"
        >
          <template #append>
            <info-tooltip :message="t('caregiverForm.AuditableDescription.office')" />
          </template>
        </v-autocomplete>

        <v-autocomplete
          :model-value="serviceCompanyId"
          variant="outlined"
          item-title="displayName"
          item-value="id"
          :custom-filter="displayNameFilter"
          :label="t('caregiver.serviceCompanyId')"
          :items="serviceCompanies"
          @update:model-value="emit('update:serviceCompanyId', $event)"
        >
          <template #append>
            <info-tooltip :message="t('caregiverForm.AuditableDescription.serviceCompany')" />
          </template>
        </v-autocomplete>
      </div>
    </v-card-text>
  </v-card>
</template>
